import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  OnboardingStatusValue,
  type OnboardingPersonalizationResponse,
  type AgentTemplate,
} from '@super/shared-types'
import { AGENT_TEMPLATES } from '@super/shared-types'
import { Button } from '@/components/ui/button'
import { FloatingActionIslandContent } from '@/components/ui/FloatingActionIslandContent'
import { onboardingClient } from '@/lib/api/onboarding.client'
import { useAppAuth } from '@/contexts/app-auth.context'
import { useOnboarding } from '../onboarding-context'

export function StandardEndingStep() {
  const { state, patch } = useOnboarding()
  const navigate = useNavigate()
  const { refetchAppContext } = useAppAuth()
  const [orderedTemplates, setOrderedTemplates] = useState<AgentTemplate[]>([])
  const [finishing, setFinishing] = useState(false)

  const handleFinish = async () => {
    setFinishing(true)
    try {
      await patch({ status: OnboardingStatusValue.COMPLETED })
      await refetchAppContext()
      navigate('/dashboard', { replace: true })
    } catch {
      setFinishing(false)
    }
  }

  useEffect(() => {
    onboardingClient
      .getPersonalization()
      .then((res: OnboardingPersonalizationResponse) => {
        const ordered = res.starterAgentIds
          .map(id => AGENT_TEMPLATES.find(t => t.id === id))
          .filter((t): t is AgentTemplate => Boolean(t))
        setOrderedTemplates(ordered)
      })
      .catch(() => setOrderedTemplates([]))
  }, [])

  const companyName = state.name ?? 'your company'

  return (
    <div className="mx-auto flex w-full max-w-[720px] flex-col gap-6 px-4">
      <div className="text-center">
        <p className="text-muted-foreground font-fragment-mono text-[10px] uppercase tracking-[0.14em]">
          SUPER · READY
        </p>
        <h1 className="font-space-grotesk mt-2 text-3xl font-semibold tracking-tight">
          Three agents, ready
        </h1>
        <p className="text-muted-foreground mt-2 text-sm">
          {companyName}'s starter agents are queued up. Turn them on one at a
          time from the dashboard when you're ready.
        </p>
      </div>

      <div className="flex flex-col gap-3">
        {orderedTemplates.map(template => (
          <TemplateRow key={template.id} template={template} />
        ))}
        {orderedTemplates.length === 0 && (
          <div className="text-muted-foreground text-center text-sm">
            Loading starter agents…
          </div>
        )}
      </div>

      <FloatingActionIslandContent
        show={true}
        componentId="onboarding-done-standard"
        priority={10}
      >
        <div className="pointer-events-auto">
          <Button onClick={handleFinish} size="sm" disabled={finishing}>
            {finishing ? 'Finishing…' : 'Finish'}
          </Button>
        </div>
      </FloatingActionIslandContent>
    </div>
  )
}

function TemplateRow({ template }: { template: AgentTemplate }) {
  return (
    <div className="border-border rounded-lg border p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-medium">{template.name}</div>
          <p className="text-muted-foreground mt-1 text-xs">
            {template.description}
          </p>
          <div className="mt-2 flex flex-wrap gap-2 text-[10px] uppercase tracking-wider">
            <span className="rounded-full bg-zinc-100 px-2 py-0.5 text-zinc-700">
              {template.direction}
            </span>
            {template.channels.map(channel => (
              <span
                key={channel}
                className="rounded-full bg-emerald-50 px-2 py-0.5 text-emerald-700"
              >
                {channel}
              </span>
            ))}
          </div>
        </div>
        <span className="text-muted-foreground text-[10px] uppercase tracking-wider">
          OFF
        </span>
      </div>
    </div>
  )
}
